import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, useRef, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { WhatsAppButton } from "../components/site/WhatsAppButton";
import { LeadPopup } from "../components/site/LeadPopup";
import { AccessibilityWidget } from "../components/accessibility-widget";
import { a11yBootScript } from "../lib/a11y-settings";



function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong. Try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a href="/" className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "NJ Epoxy Flooring Contractor | Blanco Pro Services" },
      { name: "description", content: "Top-rated NJ epoxy flooring contractors. Metallic, flake, polyaspartic & polished concrete for garages, homes & businesses. Free estimate: 908-895-6133." },
      { name: "author", content: "Blanco Pro Services" },
      { property: "og:site_name", content: "Blanco Pro Services" },
      { property: "og:type", content: "website" },

      { property: "og:title", content: "NJ Epoxy Flooring Contractor | Blanco Pro Services" },
      { name: "twitter:title", content: "NJ Epoxy Flooring Contractor | Blanco Pro Services" },
      { property: "og:description", content: "Top-rated NJ epoxy flooring contractors. Metallic, flake, polyaspartic & polished concrete for garages, homes & businesses. Free estimate: 908-895-6133." },
      { name: "twitter:description", content: "Top-rated NJ epoxy flooring contractors. Metallic, flake, polyaspartic & polished concrete for garages, homes & businesses. Free estimate: 908-895-6133." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/oDG2kkVtzvRcH5WSww6hSVWgrrj1/social-images/social-1784155261276-ChatGPT_Image_Jul_15,_2026,_06_40_40_PM.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/oDG2kkVtzvRcH5WSww6hSVWgrrj1/social-images/social-1784155261276-ChatGPT_Image_Jul_15,_2026,_06_40_40_PM.webp" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "apple-mobile-web-app-title", content: "Blanco Pro" },
    ],
    links: [
      { rel: "preload", as: "style", href: appCss, fetchPriority: "high" },
      { rel: "stylesheet", href: appCss, fetchPriority: "high" },
      // Body font: Space Grotesk (Google Fonts). Display font: TT Octosquares (self-hosted).
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;700&display=swap", fetchPriority: "high" },
      { rel: "preload", as: "font", type: "font/woff2", href: "/__l5e/assets-v1/e060d702-58e6-4919-be27-968e41de2fa7/TT_Octosquares_Regular.woff2", crossOrigin: "anonymous" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/x-icon", sizes: "96x96", href: "/favicon.ico" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-icon.png" },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
    ],
    scripts: [
      // Re-apply saved accessibility settings before first paint (no flash of default styles).
      { children: a11yBootScript },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://blancopros.com/#organization",
          name: "Blanco Pro Services",
          alternateName: "Blanco Pros",
          description:
            "New Jersey's top-rated epoxy flooring, hardscaping, and demolition contractor. Premium metallic, flake, polyaspartic, and polished concrete systems backed by a 2-year workmanship warranty.",
          url: "https://blancopros.com",
          sameAs: [
            "https://www.facebook.com/share/1S9PwsjGyB/",
            "https://www.instagram.com/blancoproservices",
          ],
          telephone: "+1-908-895-6133",
          email: "info@blancopros.com",
          priceRange: "$$",
          image: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/5a774a96-5ffd-4539-a381-f16891e14b1e",
          address: {
            "@type": "PostalAddress",
            addressRegion: "NJ",
            addressCountry: "US",
          },
          areaServed: [
            { "@type": "State", name: "New Jersey" },
            { "@type": "State", name: "Pennsylvania" },
            { "@type": "State", name: "New York" },
          ],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "5.0",
            reviewCount: "27",
          },
          openingHours: "Mo-Sa 07:30-16:30",
          knowsAbout: [
            "Epoxy Flooring",
            "Metallic Epoxy",
            "Polyaspartic Coatings",
            "Polished Concrete",
            "Garage Floor Coatings",
            "Commercial Flooring",
            "Industrial Flooring",
            "Hardscaping",
            "Concrete Demolition",
          ],
        }),
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-M8TC7ZL2"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function LeadChatWidget() {
  useEffect(() => {
    const WIDGET_ID = "6a6a93707b8e127ea93757ab";

    const mountElement = () => {
      if (document.querySelector("chat-widget")) return;
      const el = document.createElement("chat-widget");
      el.setAttribute("widget-id", WIDGET_ID);
      document.body.appendChild(el);
    };

    if (!document.querySelector('script[data-widget-id="' + WIDGET_ID + '"]')) {
      const s = document.createElement("script");
      s.src = "https://widgets.leadconnectorhq.com/loader.js";
      s.setAttribute("data-resources-url", "https://widgets.leadconnectorhq.com/chat-widget/loader.js");
      s.setAttribute("data-widget-id", WIDGET_ID);
      s.async = true;
      s.onload = mountElement;
      document.body.appendChild(s);
    }

    // Fallback in case the loader never injects the element itself.
    const t = window.setTimeout(mountElement, 2500);
    return () => window.clearTimeout(t);
  }, []);
  return null;
}

const GA_ID = "G-V7FHJY5VN4";
const GTM_ID = "GTM-M8TC7ZL2";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function loadAnalytics() {
  if ((window as unknown as { __analyticsLoaded?: boolean }).__analyticsLoaded) return;
  (window as unknown as { __analyticsLoaded?: boolean }).__analyticsLoaded = true;

  window.dataLayer = window.dataLayer || [];
  // gtag.js requires the array-like `arguments` object, not a real Array.
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }
  window.gtag = gtag as unknown as (...args: unknown[]) => void;
  window.gtag("js", new Date());
  window.gtag("config", GA_ID);


  const add = (src: string, attrs?: Record<string, string>) => {
    const j = document.createElement("script");
    j.async = true;
    j.src = src;
    if (attrs) Object.entries(attrs).forEach(([k, v]) => j.setAttribute(k, v));
    document.head.appendChild(j);
  };

  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  add(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}&l=dataLayer`);
  add(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
  add("https://links.elroi.marketing/js/external-tracking.js", {
    "data-tracking-id": "tk_192cf3fc35e74941991bada4494c2244",
  });
}

function Analytics() {
  useEffect(() => {
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    const onEv = () => {
      events.forEach((e) => window.removeEventListener(e, onEv));
      loadAnalytics();
    };
    events.forEach((e) => window.addEventListener(e, onEv, { passive: true }));
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    const timer = idle ? idle(loadAnalytics, { timeout: 3000 }) : window.setTimeout(loadAnalytics, 2500);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onEv));
      if (!idle) window.clearTimeout(timer as number);
    };
  }, []);
  return null;
}

function RouteAnalytics() {
  const routerState = useRouterState({ select: (s) => s.location.href });
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const w = window as unknown as {
      gtag?: (...args: unknown[]) => void;
      dataLayer?: unknown[];
    };
    const page_path = window.location.pathname + window.location.search;
    w.gtag?.("event", "page_view", {
      page_path,
      page_location: window.location.href,
      page_title: document.title,
    });
    w.dataLayer?.push({ event: "pageview", page_path });
  }, [routerState]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();


  return (
    <QueryClientProvider client={queryClient}>
      <Header />
      <main className="min-h-screen">
        <Outlet />
      </main>
      <Footer />
      <WhatsAppButton />
      <Analytics />
      <RouteAnalytics />
      <LeadChatWidget />
      <LeadPopup />
      <AccessibilityWidget />
    </QueryClientProvider>
  );
}

